/**
 * CoSingery — **服務提供者（公司資料）**（`decisions/043`）
 *
 * 產品負責人 2026-09-25：「my company name is **85 AI Technology Limited** … also, should add this
 *   company details into the app somewhere」
 *
 * 為何要：① 用戶有權知邊個係服務提供者 ② **卡商 audit 要求**（網站要顯示法律實體）
 *   ③ 付款畫面要寫明**收款方** ④ 收據 email 亦要顯示
 *
 * ⚠️ 紅線：
 *   · 公司資料**一律由 `GET /api/legal` 嚟**（＝config）—— **前端唔准寫死**公司名／BR／地址
 *   · 未提供嘅欄位**照實唔顯示**（唔准扮有；`missing` 由 API 照實回）
 *   · 讀唔到 API → **照實講讀唔到**（唔准靠估／唔准顯示假資料）
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LegalInfo {
  name?: string | null;
  br?: string | null;
  address?: string | null;
  email?: string | null;
  missing?: string[];
}

export default function LegalNotice({ variant = 'full' }: { variant?: 'full' | 'footer' | 'payee' }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState<LegalInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch('/api/legal');
        if (!res.ok) throw new Error(`http_${res.status}`);
        const data = (await res.json()) as LegalInfo;
        if (alive) setInfo(data);
      } catch {
        /** 讀唔到就照實講（唔准扮有公司資料） */
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (failed) {
    return (
      <p className="footnote" style={{ margin: 0 }} data-testid="legal-notice">
        {t('legal_load_failed')}
      </p>
    );
  }
  if (!info?.name) {
    /** 公司名都未有 → 唔顯示任何嘢（未設定唔准猜） */
    return null;
  }

  if (variant === 'footer') {
    return (
      <p className="footnote" style={{ margin: 0, textAlign: 'center' }} data-testid="legal-notice">
        © {new Date().getFullYear()} {info.name}
      </p>
    );
  }

  /** ⭐ 付款畫面用：一定要寫明**收款方**（卡商 audit 基本要求） */
  if (variant === 'payee') {
    return (
      <p className="footnote" style={{ margin: 0 }} data-testid="legal-payee">
        {t('pay_payee_label')}：<strong>{info.name}</strong>
      </p>
    );
  }

  return (
    <div style={{ display: 'grid', gap: '2px' }} data-testid="legal-notice">
      <p className="footnote" style={{ margin: 0 }}>
        {t('legal_provider')}：{info.name}
      </p>
      {info.br ? (
        <p className="footnote" style={{ margin: 0 }}>
          {t('legal_br')}：{info.br}
        </p>
      ) : null}
      {info.address ? (
        <p className="footnote" style={{ margin: 0 }}>
          {t('legal_address')}：{info.address}
        </p>
      ) : null}
      {info.email ? (
        <p className="footnote" style={{ margin: 0, wordBreak: 'break-all' }}>
          {t('legal_email')}：{info.email}
        </p>
      ) : null}
      {/** ⚠️ 有欄位未填 → 照實講（唔准扮有） */}
      {info.missing && info.missing.length > 0 ? (
        <p className="footnote" style={{ margin: 0 }} data-testid="legal-missing">
          {t('legal_missing')}：{info.missing.join('、')}
        </p>
      ) : null}
      <p className="footnote" style={{ margin: 0 }}>
        {t('legal_policies_pending')}
      </p>
    </div>
  );
}
